// Mock data for Aureon Media Landing Page

export const heroData = {
  eyebrow: "Now accepting new clients",
  badge: "Waiting List",
  h1: "A Simple Path from Audience to Income",
  h2: "Built for Creators",
  description: "Our launch specialists build monetization systems for Instagram creators with 20k+ followers—strategy, product, launch, and checkout—behind the scenes.",
  primaryCTA: "Apply for a Launch Partnership",
  secondaryCTA: "See How It Works",
  heroImage: "https://images.pexels.com/photos/8370328/pexels-photo-8370328.jpeg"
};

export const whatWeDoData = {
  sectionTitle: "What Our Launch Specialists Do",
  coreStatement: "We build the systems that turn your ideas into income—so you can focus on creating.",
  services: [
    {
      id: 1,
      title: "Offer & Product Strategy",
      description: "Our launch specialists help you design offers that convert—pricing, packaging, and positioning tailored for your audience. No guesswork, just clarity.",
      image: "https://images.unsplash.com/photo-1529119368496-2dfda6ec2804"
    },
    {
      id: 2,
      title: "Launch Messaging & Conversion Assets",
      description: "Sales pages, email sequences, and launch copy written in your voice—built to move your audience to action.",
      image: "https://images.unsplash.com/photo-1501504905252-473c47e087f8"
    },
    {
      id: 3,
      title: "Payments + Delivery Setup",
      description: "Checkout flows, payment processing, and automated delivery—wired end-to-end so your customers get instant access, every time.",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3"
    }
  ],
  keyBenefit: "Your launch specialist operates behind the scenes; you stay the face of the brand."
};

export const howItWorksData = {
  sectionTitle: "From Idea to Income in 4 Steps",
  intro: "A proven launch system that turns uncertainty into a structured, repeatable revenue event.",
  steps: [
    {
      number: 1,
      title: "Creator & Audience Audit",
      description: "Your launch specialist analyzes your content, audience, and existing assets to find the fastest path to revenue.",
      deliverables: "Audience profile, content audit, monetization opportunities report"
    },
    {
      number: 2,
      title: "Product Blueprint",
      description: "We design your offer—pricing, format, and positioning—so it resonates with your people.",
      deliverables: "Offer structure, pricing strategy, sales page outline"
    },
    {
      number: 3,
      title: "Launch System",
      description: "A 14-day content and story sequence engineered to build anticipation and drive sales.",
      deliverables: "14-day launch calendar, email sequences, social content templates"
    },
    {
      number: 4,
      title: "Sell & Iterate",
      description: "Your checkout goes live. Your launch specialist tracks results, optimizes conversion, and plans what's next.",
      deliverables: "Live checkout, analytics dashboard, optimization roadmap"
    }
  ],
  backgroundImage: "https://images.unsplash.com/photo-1531403009284-440f080d1e12"
};

export const revenueModelData = {
  sectionTitle: "No Upfront Fees. We Only Win When You Win.",
  explanation: "We use Whop's built-in Split system. When your audience buys, revenue is automatically distributed: 70% to you, 30% to your launch specialist. Simple, transparent, and fully aligned.",
  split: {
    creator: 70,
    aureonMedia: 30
  },
  features: [
    "Zero upfront cost",
    "Automatic revenue split",
    "Instant payouts"
  ]
};

export const toolsData = {
  sectionTitle: "Tools We Use",
  description: "Powered by leading platforms we use to accelerate launches and reduce friction.",
  tools: [
    { name: "Whop", logo: null },
    { name: "Synthetise", logo: null },
    { name: "Ghostwriter OS", logo: null },
    { name: "Flozy", logo: null }
  ]
};

export const fitCheckData = {
  sectionTitle: "Is This Right for You?",
  forYou: [
    "Instagram creators with 20k+ followers and strong engagement",
    "You want to monetize without becoming salesy",
    "You want systems instead of guesswork",
    "You want to focus on content, not infrastructure"
  ],
  notForYou: [
    "You're looking for shortcuts or gimmicks",
    "You don't want to sell to your audience",
    "You prefer to build and manage everything yourself"
  ]
};

export const faqData = {
  sectionTitle: "Frequently Asked Questions",
  faqs: [
    {
      id: 1,
      question: "What exactly do your launch specialists do?",
      answer: "Our launch specialists build complete monetization systems for creators—from offer strategy and product design to sales pages, payment processing, and automated delivery. You stay focused on creating content; your dedicated launch specialist handles the infrastructure behind the scenes."
    },
    {
      id: 2,
      question: "How long does it take to launch?",
      answer: "Most creators go from initial audit to live checkout in 4-6 weeks. The timeline depends on your content schedule and the complexity of your offer, but our structured 4-step system keeps things moving efficiently."
    },
    {
      id: 3,
      question: "What does it cost?",
      answer: "Zero upfront fees. We operate on a revenue share model: 70% to you, 30% to your launch specialist. We only make money when you make money, which means we're fully invested in your success."
    },
    {
      id: 4,
      question: "Do I need a large following?",
      answer: "We work with Instagram creators who have 20k+ followers and strong engagement. It's not just about numbers—it's about having an audience that trusts you and engages with your content."
    },
    {
      id: 5,
      question: "What platforms do you work with?",
      answer: "We primarily work with Instagram creators, but the launch systems we build (checkout, email sequences, delivery) work across all platforms. If you have an engaged audience, we can help you monetize it."
    },
    {
      id: 6,
      question: "What if I'm not sure what product to create?",
      answer: "That's exactly what Step 1 (Creator & Audience Audit) is for. Your launch specialist will analyze your content, audience demographics, and engagement patterns to identify the fastest path to revenue with an offer that fits your brand."
    },
    {
      id: 7,
      question: "What if my product doesn't sell?",
      answer: "Because we work on a revenue share model, your launch specialist is as invested in results as you are. We design offers based on proven frameworks, optimize conversion throughout the launch, and iterate based on real data. If something isn't working, we adjust and improve."
    }
  ]
};

export const finalCTAData = {
  sectionTitle: "Ready to Plan Your Next Launch?",
  description: "Let's turn your audience into a product-driven business—without adding complexity to your life.",
  primaryCTA: "Apply for a Launch Partnership"
};

export const applicationFormData = {
  fields: [
    { name: "fullName", label: "Full Name", type: "text", required: true },
    { name: "email", label: "Email Address", type: "email", required: true },
    { name: "instagramHandle", label: "Instagram Handle", type: "text", required: true },
    { name: "message", label: "Tell us about your audience", type: "textarea", required: true },
    {
      name: "hasSold",
      label: "Have you sold to your audience before?",
      type: "select",
      required: true,
      options: ["Yes", "No"]
    }
  ]
};

export const confirmationPageData = {
  headline: "Thanks — we've received your application.",
  body: "We review every application manually to ensure there's a strong fit on both sides.\n\nIf this looks like a good match, your dedicated launch specialist will reach out by email with next steps.",
  expectation: "Please note: we don't take on every creator.\nOur focus is on creators with strong engagement who are ready to launch intentionally.",
  nextSteps: "In the meantime, keep creating as usual.\nIf we move forward, your launch specialist will handle the systems — you stay focused on the audience."
};
